import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi, type Home, type LatestRelease } from '../lib/api';
import { StatusBadge } from '../components/StatusBadge';
import { RefreshCw, GitCommit } from 'lucide-react';

export function HomesPage() {
  const api = useApi();
  const [homes, setHomes] = useState<Home[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [latestRelease, setLatestRelease] = useState<LatestRelease | null>(null);
  const [showLegacy, setShowLegacy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { homes } = await api.listHomes();
      setHomes(homes);
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    api.getLatestRelease().then(setLatestRelease).catch(() => { /* non-fatal */ });
    const id = setInterval(refresh, 10000);
    const releaseId = setInterval(() => {
      api.getLatestRelease().then(setLatestRelease).catch(() => { /* non-fatal */ });
    }, 60_000);
    return () => {
      clearInterval(id);
      clearInterval(releaseId);
    };
  }, []);

  // Hide deprecated/legacy homes (e.g. the legacy 720FrontRd, superseded by 720FrontRdNew)
  // by default — they clutter the fleet view. A toggle reveals them when needed.
  const legacyCount = homes.filter((h) => h.isLegacy).length;
  const visibleHomes = showLegacy ? homes : homes.filter((h) => !h.isLegacy);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-brand-ink">Homes</h1>
        <div className="flex items-center gap-3">
          {latestRelease?.manifest && (
            <Link
              to="/edges"
              className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-brand-ink px-2.5 py-1 rounded-lg border border-slate-200 hover:border-slate-400"
              title={`Latest edge release on ${latestRelease.channel}: ${latestRelease.manifest.subject}`}
            >
              <GitCommit size={12} className="text-slate-500" />
              <span>Latest <span className="font-mono">{latestRelease.manifest.shaShort}</span></span>
            </Link>
          )}
          {legacyCount > 0 && (
            <button
              onClick={() => setShowLegacy((v) => !v)}
              className="text-xs text-slate-600 hover:text-brand-ink px-2.5 py-1 rounded-lg border border-slate-200 hover:border-slate-400"
              title={showLegacy ? 'Hide deprecated/legacy homes' : 'Show deprecated/legacy homes'}
            >
              {showLegacy ? 'Hide legacy' : `Show legacy (${legacyCount})`}
            </button>
          )}
          <button
            onClick={refresh}
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-ink px-3 py-1.5 rounded-lg border border-slate-300 hover:border-slate-400"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-ink-800 uppercase text-xs tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Home ID</th>
              <th className="text-left px-4 py-3 font-medium">Display name</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Edge</th>
              <th className="text-left px-4 py-3 font-medium">AWS account</th>
              <th className="text-left px-4 py-3 font-medium">Created</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {visibleHomes.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-600">
                  No homes yet. Click "New Home" to provision one.
                </td>
              </tr>
            )}
            {visibleHomes.map((h) => (
              <tr key={h.homeId} className="border-t border-slate-200 hover:bg-slate-100">
                <td className="px-4 py-3 font-mono text-brand-ink">{h.homeId}</td>
                <td className="px-4 py-3 text-brand-ink">{h.displayName}</td>
                <td className="px-4 py-3"><StatusBadge status={h.status} /></td>
                <td className="px-4 py-3"><EdgeCell home={h} /></td>
                <td className="px-4 py-3 font-mono text-ink-800">{h.accountIds?.test ?? <span className="text-slate-500">—</span>}</td>
                <td className="px-4 py-3 text-ink-800">{h.createdAt?.slice(0, 10) ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EdgeCell({ home }: { home: Home }) {
  const e = home.edge;
  if (!e || !e.currentShaShort) {
    return <span className="text-slate-500 text-xs">—</span>;
  }
  const ageSec = e.lastHeartbeatAt
    ? (Date.now() - new Date(e.lastHeartbeatAt).getTime()) / 1000
    : Infinity;
  const dot =
    ageSec < 10 * 60 ? 'bg-emerald-500'
    : ageSec < 60 * 60 ? 'bg-amber-500'
    : 'bg-red-500';
  const title = [
    e.currentShaSubject ? `"${e.currentShaSubject}"` : '',
    e.lastDeployAt ? `Deployed ${e.lastDeployAt}` : '',
    e.lastHeartbeatAt ? `Last seen ${e.lastHeartbeatAt}` : '',
  ].filter(Boolean).join('\n');
  return (
    <span className="inline-flex items-center gap-1.5 text-xs" title={title}>
      <span className={`inline-block w-2 h-2 rounded-full ${dot}`} />
      <span className="font-mono text-brand-ink">{e.currentShaShort}</span>
    </span>
  );
}
